package com.bikompanion.ui.composables

import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.vector.Path
import androidx.compose.ui.graphics.vector.PathData
import androidx.compose.ui.graphics.vector.VectorPainter
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.image
import org.maplibre.compose.expressions.value.IconPitchAlignment
import org.maplibre.compose.expressions.value.IconRotationAlignment
import org.maplibre.compose.expressions.value.SymbolAnchor
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.layers.LocationIndicatorLayer
import org.maplibre.compose.layers.SymbolLayer
import org.maplibre.compose.location.LocationMeasurement
import org.maplibre.compose.map.LocalMapState
import org.maplibre.compose.map.MapState
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.overlay.AttributionDefaults
import org.maplibre.compose.overlay.CompassDefaults
import org.maplibre.compose.overlay.DisappearingCompassButton
import org.maplibre.compose.overlay.ExpandingAttributionButton
import org.maplibre.compose.overlay.MaplibreLogo
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.compose.style.BaseStyle
import org.maplibre.compose.util.MaplibreComposable
import org.maplibre.spatialk.geojson.Feature
import org.maplibre.spatialk.geojson.FeatureCollection
import org.maplibre.spatialk.geojson.Point
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

/** The basemap and its Compose presentation. */
@Composable
internal fun MapLayer(
    mapState: MapState,
    darkTheme: Boolean,
    onStationTap: (DpOffset) -> ClickResult,
    onCompassClick: () -> Unit = {},
    showBuiltInOverlay: Boolean = false,
) {
    configureMapProjection(mapState)
    MaplibreMap(
        state = mapState,
        uiOptions = mapOptions(),
        // Markers are drawn into a Canvas that must not steal the map's gestures, so hits
        // are resolved here instead of by a pointer handler sitting on top of the map.
        interactions = MapInteractions {
            callbacks { click { onEvent { onStationTap(it.screenOffset) } } }
        },
        // The default overlay spreads the logo and attribution button across the full
        // width, putting the attribution button under the locate-me FAB in the opposite
        // corner. Stacking them in one column in the top-end corner keeps both clear of
        // the FAB and the search bar.
        overlay = {
            if (showBuiltInOverlay) {
                Column(
                    modifier = Modifier.align(Alignment.TopEnd),
                    horizontalAlignment = Alignment.End,
                ) {
                    MaplibreLogo()
                    // The default toggle button tints the info icon black, which disappears
                    // over a dark basemap while the button is collapsed. Match the icon to
                    // the theme instead.
                    val iconTint = if (darkTheme) Color.White else AttributionDefaults.ContentColor
                    ExpandingAttributionButton(
                        contentAlignment = Alignment.TopEnd,
                        toggleButton = { onClick ->
                            Box(
                                Modifier
                                    .size(40.dp)
                                    .clip(RoundedCornerShape(percent = 50))
                                    .clickable(onClick = onClick, role = Role.Button),
                                contentAlignment = Alignment.Center,
                            ) {
                                Image(
                                    painter = AttributionDefaults.icon(),
                                    contentDescription = AttributionDefaults.contentDescription(),
                                    modifier = Modifier.size(24.dp),
                                    contentScale = ContentScale.Fit,
                                    colorFilter = ColorFilter.tint(iconTint),
                                )
                            }
                        },
                    )
                    // Only on screen while the map is actually turned or tilted — a compass that is
                    // always there is a control pointing at the one thing that has not changed, and
                    // it would sit over the basemap for the whole time the map is north-up, which is
                    // almost all of it. A tap turns the map back to north, and tells the screen so
                    // that a heading-follow that is running does not immediately turn it away again.
                    DisappearingCompassButton(
                        onClick = onCompassClick,
                        style = CompassDefaults.style().copy(
                            containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.9f),
                        ),
                        modifier = Modifier.padding(top = 4.dp, end = 4.dp),
                    )
                }
            }
        },
        modifier = Modifier.fillMaxSize(),
    )
}

/** Controls drawn over the screen-edge blur rather than captured into the map source. */
@Composable
internal fun MapLayerControls(
    mapState: MapState,
    darkTheme: Boolean,
    onCompassClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    CompositionLocalProvider(LocalMapState provides mapState) {
        Column(
            modifier = modifier,
            horizontalAlignment = Alignment.End,
        ) {
            MaplibreLogo()
            val iconTint = if (darkTheme) Color.White else AttributionDefaults.ContentColor
            ExpandingAttributionButton(
                contentAlignment = Alignment.TopEnd,
                toggleButton = { onClick ->
                    Box(
                        Modifier
                            .size(40.dp)
                            .clip(RoundedCornerShape(percent = 50))
                            .clickable(onClick = onClick, role = Role.Button),
                        contentAlignment = Alignment.Center,
                    ) {
                        Image(
                            painter = AttributionDefaults.icon(),
                            contentDescription = AttributionDefaults.contentDescription(),
                            modifier = Modifier.size(24.dp),
                            contentScale = ContentScale.Fit,
                            colorFilter = ColorFilter.tint(iconTint),
                        )
                    }
                },
            )
            DisappearingCompassButton(
                onClick = onCompassClick,
                style = CompassDefaults.style().copy(
                    containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.9f),
                ),
                modifier = Modifier.padding(top = 4.dp, end = 4.dp),
            )
        }
    }
}

/** How far the beam reaches from the dot, and how wide it opens. */
private val HeadingConeLength = 48.dp
private const val HeadingConeHalfAngle = 32f
private val UserLocationColor = Color(0xFF2196F3)

/** The number of straight edges the arc at the far end of the beam is drawn with. */
private const val HeadingConeArcSegments = 24

/** The rider: a dot, its accuracy, and the beam saying which way they are facing. */
@Composable
@MaplibreComposable
internal fun UserLocationPuck(
    location: LocationMeasurement?,
    heading: () -> Float?,
    showCone: Boolean,
) {
    val currentHeading = heading()
    if (showCone && currentHeading != null && location != null) {
        val conePainter = rememberHeadingConePainter()
        // The rotation is a layer property rather than a feature property, so the point carries
        // nothing beyond where it is.
        val source = rememberGeoJsonSource(
            GeoJsonData.Features(
                FeatureCollection(
                    Feature(geometry = Point(location.position), properties = null),
                ),
            ),
        )
        SymbolLayer(
            id = "user-heading-cone",
            source = source,
            iconImage = image(conePainter),
            iconAnchor = const(SymbolAnchor.Center),
            // Both aligned to the map: the beam is a claim about the ground ahead of the rider,
            // so it turns with north and lies down with the tilt instead of staying flat on the
            // glass. Drawn pointing up, its rotation is the heading itself.
            iconRotationAlignment = const(IconRotationAlignment.Map),
            iconPitchAlignment = const(IconPitchAlignment.Map),
            iconRotate = const(currentHeading),
            // The beam is the rider's own, not a label competing for room with the station
            // markers: it is never the one that gets dropped.
            iconAllowOverlap = const(true),
            iconIgnorePlacement = const(true),
        )
    }
    LocationIndicatorLayer(
        id = "user-location",
        location = location?.position,
        accuracyRadius = location?.horizontalAccuracy,
        // The beam above says where they are facing; the library's own arrow would say it a
        // second time, in a different shape, on top of the dot.
        bearingImage = null,
        accuracyRadiusBorderColor = UserLocationColor,
    )
}

/**
 * The beam artwork: a wedge with its point at the centre of the image, fading out as it opens.
 *
 * Centred rather than drawn from the bottom edge because the symbol is anchored at the rider,
 * and an anchor is a point in the image — putting the point of the wedge there is what keeps
 * the beam leaving the dot rather than passing through it.
 */
@Composable
private fun rememberHeadingConePainter(): VectorPainter =
    rememberVectorPainter(
        defaultWidth = HeadingConeLength * 2,
        defaultHeight = HeadingConeLength * 2,
        autoMirror = false,
    ) { viewportWidth, viewportHeight ->
        val apexX = viewportWidth / 2f
        val apexY = viewportHeight / 2f
        val radius = viewportHeight / 2f
        Path(
            // Straight edges rather than an arc: at this size the difference is under a pixel,
            // and the vector arc builder wants ellipse radii and sweep flags for the same shape.
            pathData = PathData {
                moveTo(apexX, apexY)
                repeat(HeadingConeArcSegments + 1) { step ->
                    val fraction = step.toFloat() / HeadingConeArcSegments
                    // Zero points up the image, which is north once the symbol is map-aligned.
                    val degrees = -HeadingConeHalfAngle + fraction * 2 * HeadingConeHalfAngle
                    val radians = degrees * PI.toFloat() / 180f
                    lineTo(apexX + radius * sin(radians), apexY - radius * cos(radians))
                }
                close()
            },
            fill = Brush.radialGradient(
                0f to UserLocationColor.copy(alpha = 0.5f),
                1f to Color.Transparent,
                center = Offset(apexX, apexY),
                radius = radius,
            ),
        )
    }

private const val TileApiKey = "cb1_28vy_1_c8f6e2cc7e836c0b4ae35662"

internal fun buildMapStyle(dark: Boolean): BaseStyle {
    val variant = if (dark) "dark-matter" else "positron"
    return BaseStyle.Uri("https://basemaps.cartocdn.com/gl/$variant-gl-style/style.json?key=$TileApiKey")
}
